import Bus from '@/core/bus';
import Vue from 'vue';
import { BusConstants, StorageConstants } from '@/core/constants';
import { Jwt } from '@/models/jwt/jwt.model';
import { Role } from '@/core/security/Role';
import { User } from '@/models/user/user.model';
import { ISignalRClient } from '@/models/website/SignalRClient';
import { NotificationApi } from '@/services/api/api.notifications';
import { UserApi } from '@/services/api/api.user';
import { JwtService } from '@/services/jwt/jwt.service';
import SignalRService from '@/services/signalR/signalR.service';
import { LocalStorageService } from '@/services/storage/storage.service';
import TimeService from '@/services/time/time.service';
import store from '@/store';
import {
    AccountViewObject,
    GameAccountData,
    NotificationViewObject,
    UpdateUsernameRequest,
    UpdateUserRequest,
    UserLoginRequest,
    UserLoginViewModel,
    UserPermission,
    UserRegisterRequest,
    UserViewObject,
    VoteSiteViewObject,
    VoteTimerViewObject,
    UserUpdateSettingsRequest,
    PayPalLogViewModel,
    VoteViewObject,
    UserResetPasswordRequest
} from '@/types/apiServerContract';
import { Action, getModule, Module, Mutation, MutationAction, VuexModule } from 'vuex-module-decorators';
import { PayPalApi } from '@/services/api/api.paypal';

@Module({ namespaced: true, dynamic: true, store, name: 'UserModule' })
class UserState extends VuexModule {
	account: AccountViewObject | null = null;
	user: User | null = null;
	token: Jwt | null = null;
	voteSites: readonly VoteSiteViewObject[] = [];
	voteTimers: VoteTimerViewObject[] = [];
	connections: readonly ISignalRClient[] = [];
	lastNewsComment: Date | null = null;
	notifications: NotificationViewObject[] = [];

	get accountData(): GameAccountData | undefined {
	    return this.account?.accountData;
	}

	get IsLoggedIn(): boolean {
	    return this.user != null && this.token != null;
	}

	get Roles(): Role[] {
	    return this.user?.roles ?? [];
	}

	get Permissions(): UserPermission[] {
	    return this.user?.permissions ?? [];
	}

	get Token(): string {
	    return this.token?.token ?? '';
	}

	get activeIp(): string {
	    return this.user?.currentLogin?.ip ?? '';
	}

	get unseenNotifications(): NotificationViewObject[] {
	    return [...this.notifications].filter(x => !x.seen);
	}

	@Mutation
	HandleLoginResponse(result: UserLoginViewModel) {
	    this.SaveUser(result.user);
	    this.SaveAccount(result.account);
	    this.SaveToken(JwtService.DecodeTokenAsJwt(result.token));
	}

	@Mutation
	SaveUser(user: UserViewObject | null = null) {
	    this.user = user
	        ? new User(
	            user.id,
	            user.userName,
	            user.accountId,
	            user.currentLogin,
	            user.email,
	            user.emailHidden,
	            user.fileAccess,
	            new Date(user.joinDate),
	            user.lastLogin,
	            user.location,
	            user.lockoutEnd ? new Date(user.lockoutEnd) : null as any,
	            user.lockoutEnabled,
	            user.permissions,
	            user.realms,
	            user.roles as Role[],
	            user.settings,
	            user.totalVotes,
	            user.hasAcceptedDonationTerms
			  )
	        : null;

	    if (user) {
	        LocalStorageService.setItem(StorageConstants.UserData, JSON.stringify(user));

	        Vue.prototype.$user = Object.freeze(user);
	    } else {
	        LocalStorageService.removeItem(StorageConstants.UserData);

	        Vue.prototype.$user = null;
	    }
	}

	@Mutation
	SaveAccount(account: AccountViewObject | null = null) {
	    this.account = account;

	    if (account) {
	        LocalStorageService.setItem(StorageConstants.Account, JSON.stringify(account));
	    } else {
	        LocalStorageService.removeItem(StorageConstants.Account);
	    }
	}

	@Mutation
	SaveAccountData(accountData: GameAccountData) {
	    if (!this.account) return;

	    this.account.accountData = accountData;

	    LocalStorageService.setItem(StorageConstants.Account, JSON.stringify(this.account));
	}

	@Mutation
	SaveToken(token: Jwt | null = null) {
	    this.token = Object.freeze(token);

	    if (token) {
	        LocalStorageService.setItem(StorageConstants.Token, token.token);
	    } else {
	        LocalStorageService.removeItem(StorageConstants.Token);
	    }
	}

	@Mutation
	UpdateCurrentSession(client: ISignalRClient) {
	    if (!this.user?.currentLogin) return;

	    this.user.currentLogin.ip = client.ipAddress;
	    this.user.currentLogin.date = client.connected;
	}

	@Mutation
	SynchronizeNotifications(notifications: NotificationViewObject[]) {
	    this.notifications = notifications;
	}

	@Mutation
	NotificationReceived(notification: NotificationViewObject) {
	    this.notifications.unshift(notification);
	}

	@Mutation
	NotificationSeen(notification: NotificationViewObject) {
	    const entity = this.notifications.find(x => x.id === notification.id);
	    if (entity) {
	        entity.seen = true;
	    }
	}

	@Mutation
	MarkAllNotificationsAsRead() {
	    this.notifications.forEach(n => (n.seen = true));
	}

	@Mutation
	SetNewsCommentTimer(seconds: number) {
	    this.lastNewsComment = new Date(new Date().getTime() + seconds * 1000);

	    LocalStorageService.setItem(StorageConstants.LastNewsComment, this.lastNewsComment);
	}

	@Action({ rawError: true })
	async Login(request: UserLoginRequest): Promise<UserLoginViewModel> {
	    const result = await UserApi.Login(request);
	    this.SaveUser(result.user);
	    this.SaveAccount(result.account);
	    this.SaveToken(JwtService.DecodeTokenAsJwt(result.token));
	    Bus.emit(BusConstants.OnLogin, result);

	    await SignalRService.reconnect();
	    return result;
	}

	@Action({ rawError: true })
	async Register(request: UserRegisterRequest): Promise<UserLoginViewModel> {
	    const result = await UserApi.Register(request);
	    this.SaveUser(result.user);
	    this.SaveAccount(result.account);
	    this.SaveToken(JwtService.DecodeTokenAsJwt(result.token));
	    Bus.emit(BusConstants.OnLogin, result);

	    await SignalRService.reconnect();
	    return result;
	}

	@Action({ rawError: true })
	async Logout() {
	    try {
	        await UserApi.Logout();
	    } finally {
	        this.Reset();

	        await SignalRService.reconnect();

	        Bus.emit(BusConstants.OnLogout);
	    }
	}

	@Action({ rawError: true })
	async ResetPassword(request: UserResetPasswordRequest) {
	    const result = await UserApi.ResetPassword(request);
	    this.SaveUser(result.user);
	    this.SaveAccount(result.account);
	    this.SaveToken(JwtService.DecodeTokenAsJwt(result.token));
	    Bus.emit(BusConstants.OnLogin, result);

	    await SignalRService.reconnect();
	    return result;
	}

	@Action({ rawError: true })
	async RefreshToken(token: string): Promise<boolean> {
	    await SignalRService.stop();

	    this.SaveToken(JwtService.DecodeTokenAsJwt(token));

	    await SignalRService.start();

	    Bus.emit(BusConstants.OnLogin); // Trigger an on-login hook as we got a new token so basically a new login state
	    return true;
	}

	@Action({ rawError: true })
	async GetUserData() {
	    const result = await UserApi.GetUserData();
	    this.SaveUser(result.user);
	    this.SaveAccount(result.account);
	}

	@Mutation
	HandleUpdateUserResponse(request: UpdateUserRequest) {
	    const { location, username } = request;
		this.user!.location = location;
		this.user!.userName = username;
	}

	@Action({ rawError: true })
	async UpdateUser(request: UpdateUserRequest) {
	    await UserApi.UpdateUser(request);
	    this.HandleUpdateUserResponse(request);
	    this.SaveUser(this.user);
	}

	@Mutation
	HandleUpdateUserSettingsResponse(request: UserUpdateSettingsRequest) {
	    if (this.user) {
	        this.user.settings = request.settings;
	    }
	}

	@Action({ rawError: true })
	async UpdateUserSettings(request: UserUpdateSettingsRequest) {
	    await UserApi.UpdateUserSettings(request);
	    this.HandleUpdateUserSettingsResponse(request);
	    this.SaveUser(this.user);
	}

	@Mutation
	UpdateIngameUsername(username: string) {
	    if (this.account) {
	        this.account.username = username;
	    }
	}

	@Action({ rawError: true })
	async UpdateIngameAccountUsername(request: UpdateUsernameRequest) {
	    await UserApi.UpdateIngameAccountUsername(request);
	    this.UpdateIngameUsername(request.newUsername);
	    this.SaveAccount(this.account);
	}

	@Mutation
	HandleVoteResponse(data: VoteViewObject) {
		this.account!.accountData!.vp = data.vp;
		this.user!.totalVotes += 1;
		this.voteTimers.push({
		    site: data.site.id,
		    unsetTimer: data.unsetTimer
		});
	}

	@Action({ rawError: true })
	async Vote(site: VoteSiteViewObject) {
	    await UserApi.Vote(site);
	}

	@MutationAction({ mutate: ['notifications'], rawError: true })
	async GetNotifications() {
	    const result = await NotificationApi.GetNotifications();
	    return { notifications: result.notification };
	}

	@MutationAction({ mutate: ['voteSites'] })
	async GetVoteSites() {
	    const result = await UserApi.GetVoteSites();
	    return { voteSites: Object.freeze(result.voteSites) };
	}

	@MutationAction({ mutate: ['voteTimers'] })
	async GetVoteTimers() {
	    const result = await UserApi.GetVoteTimers();
	    return { voteTimers: result.timers };
	}

	get AvailableVoteSites(): number {
	    if (!this.IsLoggedIn) return 0;

	    const now = TimeService.Unix;
	    const voteTimers = this.voteTimers.filter(x => x.unsetTimer > now);
	    return this.voteSites.length - voteTimers.length;
	}

	@Mutation
	handleAcceptDonationTerms() {
		this.user!.hasAcceptedDonationTerms = true;
	}

	@Action({ rawError: true })
	async acceptDonationTerms() {
	    await UserApi.AcceptDonationTerms();
	    this.handleAcceptDonationTerms();
	}

	@Mutation
	handleDonationSuccess(log: PayPalLogViewModel) {
		this.account!.accountData.dp = log.totalDp;
	}

	@Action({ rawError: true })
	async Donate(request: { paymentId: string, payerId: string, token: string }) {
	    const result = await PayPalApi.ExecutePayment(request.paymentId, request.payerId, request.token);
	    this.handleDonationSuccess(result);
	    return result;
	}

	@Mutation
	SetActiveConnections(connections: ISignalRClient[]) {
	    this.connections = Object.freeze(connections);
	}

	@Mutation
	Reset() {
	    this.user = null;
	    this.token = null;
	    this.account = null;
	    this.voteTimers = [];
	    this.connections = [];

	    LocalStorageService.removeItem(StorageConstants.UserData);
	    LocalStorageService.removeItem(StorageConstants.Account);
	    LocalStorageService.removeItem(StorageConstants.AccountData);
	    LocalStorageService.removeItem(StorageConstants.Token);
	}

	@Action({ rawError: true })
	Clear() {
	    this.Reset();
	}

	@Action({ rawError: true })
	async Init() {
	    const user = LocalStorageService.getItemAs<User>(StorageConstants.UserData);
	    this.SaveUser(user);
	    const account = LocalStorageService.getItemAs<AccountViewObject>(StorageConstants.Account);
	    this.SaveAccount(account);
	    const token = JwtService.DecodeTokenAsJwt(LocalStorageService.getItem(StorageConstants.Token));
	    this.SaveToken(token);
	    const lastNewsTime = LocalStorageService.getItem(StorageConstants.LastNewsComment);
	    if (lastNewsTime) {
	        const now = new Date();
	        const then = new Date(lastNewsTime);
	        const diff = Math.round((then.getTime() - now.getTime()) / 1000);
	        if (diff > 0) {
	            this.SetNewsCommentTimer(diff);
	        }
	    }

	    if (this.IsLoggedIn) {
	        this.GetUserData();
	        this.GetVoteSites();
	        this.GetVoteTimers();
	    }
	}
}

const UserModule = getModule(UserState);

export { UserModule };
